import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import RewardGrid from '../components/RewardGrid';
import SystemSettings from '../components/SystemSettings';
import {
  saveRewardsDraft,
  loadRewardsDraft,
  clearRewardsDraft,
} from '../utils/rewardDraftStorage';
import { DEFAULT_CHECKIN_RULES_HTML } from '../components/checkinRulesDefaults';
import './Settings.css';

function parseRewardsSaveError(err, resData) {
  if (resData && typeof resData.message === 'string' && resData.message) {
    return resData.message;
  }
  const d = err?.response?.data;
  if (d && typeof d.message === 'string' && d.message) return d.message;
  if (typeof d === 'string' && d) return d;
  if (err?.message) return err.message;
  return 'Failed to save rewards';
}

function SettingsBackBar() {
  return (
    <div className="settings-back-bar">
      <Link to="/" className="settings-back-chevron" aria-label="Back to dashboard">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </Link>
      <span className="settings-back-title">Dashboard</span>
    </div>
  );
}

const DEFAULT_REWARD = (day) => ({
  day,
  type: '',
  tokenId: '',
  tokenName: '',
  tokenSymbol: '',
  tokenAmount: 0,
  tokenIcon: '',
  voucherId: '',
  campaignVoucherSchemaId: '',
  campaignId: '',
  voucherName: '',
  voucherImage: '',
  voucherValue: 0,
  voucherValueCurrency: '',
  voucherValueType: '',
  voucherType: '',
  voucherCategory: '',
});

function mapRewardFromApi(r) {
  return {
    ...r,
    tokenIcon: r.tokenIcon ?? '',
    campaignVoucherSchemaId: r.campaignVoucherSchemaId ?? '',
    campaignId: r.campaignId ?? '',
    voucherImage: r.voucherImage ?? '',
    voucherValue: r.voucherValue ?? 0,
    voucherValueCurrency: r.voucherValueCurrency ?? '',
    voucherValueType: r.voucherValueType ?? '',
    voucherType: r.voucherType ?? '',
    voucherCategory: r.voucherCategory ?? '',
  };
}

/** Stable shape for comparing reward rows (server vs editor state). */
function normalizeRewardRow(r) {
  const d = r || {};
  return {
    day: Number(d.day) || 0,
    type: String(d.type || ''),
    tokenId: String(d.tokenId ?? ''),
    tokenName: String(d.tokenName ?? ''),
    tokenSymbol: String(d.tokenSymbol ?? ''),
    tokenAmount: Number(d.tokenAmount) || 0,
    tokenIcon: String(d.tokenIcon ?? ''),
    voucherId: String(d.voucherId ?? ''),
    campaignVoucherSchemaId: String(d.campaignVoucherSchemaId ?? ''),
    campaignId: String(d.campaignId ?? ''),
    voucherName: String(d.voucherName ?? ''),
    voucherImage: String(d.voucherImage ?? ''),
    voucherValue: Number(d.voucherValue) || 0,
    voucherValueCurrency: String(d.voucherValueCurrency ?? ''),
    voucherValueType: String(d.voucherValueType ?? ''),
    voucherType: String(d.voucherType ?? ''),
    voucherCategory: String(d.voucherCategory ?? ''),
  };
}

function rewardsSnapshot(arr) {
  return JSON.stringify((arr || []).map(normalizeRewardRow));
}

function rewardsEqual(a, b) {
  return rewardsSnapshot(a) === rewardsSnapshot(b);
}

function cloneRewardsBaseline(arr) {
  return JSON.parse(JSON.stringify((arr || []).map(normalizeRewardRow)));
}

function normalizeSystemSnapshot(s) {
  const rules =
    s.rulesContent && String(s.rulesContent).trim().length > 0
      ? String(s.rulesContent)
      : DEFAULT_CHECKIN_RULES_HTML;
  return {
    enableCheckIn: !!s.enableCheckIn,
    resetIfMissed: s.resetIfMissed !== false,
    rulesContent: rules,
  };
}

function systemSnapshotsEqual(a, b) {
  return JSON.stringify(normalizeSystemSnapshot(a)) === JSON.stringify(normalizeSystemSnapshot(b));
}

function Settings() {
  const [activeTab, setActiveTab] = useState('rewards');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rewards, setRewards] = useState(
    Array.from({ length: 7 }, (_, i) => DEFAULT_REWARD(i + 1))
  );
  const [system, setSystem] = useState({
    enableCheckIn: true,
    resetIfMissed: true,
    rulesContent: '',
  });

  const [savingRewards, setSavingRewards] = useState(false);
  const [savingSystem, setSavingSystem] = useState(false);
  const [toast, setToast] = useState(null);
  const [rewardsSaveError, setRewardsSaveError] = useState(null);
  const [localDraft, setLocalDraft] = useState(null);
  /** Same origin backend uses for VIO `/uploads/...` (GET /checkin/settings). */
  const [vioAssetBaseUrl, setVioAssetBaseUrl] = useState('');
  /** Last saved / loaded rewards (normalized) — used to detect edits. */
  const [rewardsBaseline, setRewardsBaseline] = useState(null);
  /** Last saved / loaded system settings (normalized rules + toggles). */
  const [systemBaseline, setSystemBaseline] = useState(null);

  const refreshLocalDraft = useCallback(() => {
    setLocalDraft(loadRewardsDraft());
  }, []);

  const showToast = (message, type = 'success', durationMs = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), durationMs);
  };

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/checkin/settings');
      if (res.data.status === 'Success') {
        const data = res.data.message;

        if (typeof data.vioPublicBaseUrl === 'string') {
          setVioAssetBaseUrl(data.vioPublicBaseUrl);
        }

        if (data.rewards && data.rewards.length === 7) {
          const mapped = data.rewards.map(mapRewardFromApi);
          setRewards(mapped);
          setRewardsBaseline(cloneRewardsBaseline(mapped));
        }

        if (data.system) {
          const sys = {
            enableCheckIn: data.system.enableCheckIn ?? true,
            resetIfMissed: data.system.resetIfMissed ?? true,
            rulesContent: data.system.rulesContent || '',
          };
          setSystem(sys);
          setSystemBaseline(normalizeSystemSnapshot(sys));
        }
      } else {
        setError(res.data.message || 'Failed to load settings');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!loading) refreshLocalDraft();
  }, [loading, refreshLocalDraft]);

  const handleSaveRewards = async () => {
    try {
      setSavingRewards(true);
      setRewardsSaveError(null);
      const res = await api.put('/checkin/settings/rewards', { rewards });
      if (res.data.status === 'Success') {
        clearRewardsDraft();
        refreshLocalDraft();
        const msg = res.data.message;
        if (msg && typeof msg.vioPublicBaseUrl === 'string') {
          setVioAssetBaseUrl(msg.vioPublicBaseUrl);
        }
        if (msg?.rewards?.length === 7) {
          const mapped = msg.rewards.map(mapRewardFromApi);
          setRewards(mapped);
          setRewardsBaseline(cloneRewardsBaseline(mapped));
        }
        showToast('Rewards saved successfully');
      } else {
        const msg = parseRewardsSaveError(null, res.data);
        setRewardsSaveError(msg);
        saveRewardsDraft(rewards);
        refreshLocalDraft();
        showToast(
          `${msg} Your work was saved as a local draft.`,
          'error',
          8000
        );
      }
    } catch (err) {
      const msg = parseRewardsSaveError(err, null);
      setRewardsSaveError(msg);
      saveRewardsDraft(rewards);
      refreshLocalDraft();
      showToast(
        `${msg} Your work was saved as a local draft.`,
        'error',
        8000
      );
    } finally {
      setSavingRewards(false);
    }
  };

  const handleSaveRewardsDraft = () => {
    saveRewardsDraft(rewards);
    refreshLocalDraft();
    showToast('Draft saved in this browser (not uploaded).', 'success');
  };

  const handleRestoreRewardsDraft = () => {
    const d = loadRewardsDraft();
    if (!d?.rewards) return;
    setRewards(d.rewards);
    setRewardsSaveError(null);
    refreshLocalDraft();
    showToast('Draft restored.');
  };

  const handleDiscardRewardsDraft = () => {
    clearRewardsDraft();
    refreshLocalDraft();
    showToast('Local draft discarded.');
  };

  const handleSaveSystem = async () => {
    try {
      setSavingSystem(true);
      const rulesContent =
        system.rulesContent && String(system.rulesContent).trim().length > 0
          ? system.rulesContent
          : DEFAULT_CHECKIN_RULES_HTML;
      const res = await api.put('/checkin/settings/system', { ...system, rulesContent });
      if (res.data.status === 'Success') {
        const next = { ...system, rulesContent };
        setSystem(next);
        setSystemBaseline(normalizeSystemSnapshot(next));
        showToast('System settings saved successfully');
      } else {
        showToast(res.data.message || 'Failed to save system settings', 'error');
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || 'Failed to save system settings',
        'error'
      );
    } finally {
      setSavingSystem(false);
    }
  };

  const rewardsDirty = useMemo(() => {
    if (rewardsBaseline === null) return false;
    return !rewardsEqual(rewards, rewardsBaseline);
  }, [rewards, rewardsBaseline]);

  const systemDirty = useMemo(() => {
    if (systemBaseline === null) return false;
    return !systemSnapshotsEqual(system, systemBaseline);
  }, [system, systemBaseline]);

  if (loading) {
    return (
      <div className="settings-page">
        <SettingsBackBar />
        <div className="loading-container">
          <div className="spinner" />
          Loading settings...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-page">
        <SettingsBackBar />
        <div className="settings-error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchSettings}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <SettingsBackBar />

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
        >
          Reward Configuration
        </button>
        <button
          className={`settings-tab ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          System Management
        </button>
      </div>

      <div className="card settings-body">
        {activeTab === 'rewards' && (
          <RewardGrid
            rewards={rewards}
            onChange={setRewards}
            onSave={handleSaveRewards}
            saving={savingRewards}
            saveError={rewardsSaveError}
            onDismissSaveError={() => setRewardsSaveError(null)}
            localDraft={localDraft}
            onSaveDraft={handleSaveRewardsDraft}
            onRestoreDraft={handleRestoreRewardsDraft}
            onDiscardDraft={handleDiscardRewardsDraft}
            vioAssetBaseUrl={vioAssetBaseUrl}
            hasUnsavedChanges={rewardsDirty}
          />
        )}
        {activeTab === 'system' && (
          <SystemSettings
            settings={system}
            onChange={setSystem}
            onSave={handleSaveSystem}
            saving={savingSystem}
            hasUnsavedChanges={systemDirty}
          />
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default Settings;
